import React from 'react';
import { Drawer, DrawerProps as MuiDrawerProps, useTheme } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Box } from '../Box';
import { IconButton } from '../IconButton';

export interface SideSheetProps extends Omit<MuiDrawerProps, keyof PounceStyleProps | 'anchor'> {
  /** Whether the modal should be visible or not */
  open: boolean;

  /**  Callback fired when the component requests to be closed */
  onClose: () => void;

  /** Whether the modal should display the dismissIcon */
  hideDismissIcon?: boolean;

  /** The id of the HTML node that contains the title of the modal */
  'aria-labelledby'?: string;

  /** The id of the HTML node that contains the text of the modal */
  'aria-describedby'?: string;

  /** The extra container props e.g width */
  containerProps?: PounceStyleProps;

  /** The content of the Sidesheet */
  children?: React.ReactNode;

  /** Side from which the drawer will appear */
  anchor?: 'left' | 'right' | 'top' | 'bottom';

  /** The width of the Sidesheet */
  width?: string;

  /** Additional styles to apply to the Drawer */
  sx?: MuiDrawerProps['sx'];
}

function drawerSxToArray(sx: MuiDrawerProps['sx']) {
  if (sx == null) {
    return [];
  }
  return Array.isArray(sx) ? sx : [sx];
}

/**
 * A dialog variation that helps with grabbing the user's attention by blurring the background and
 * presenting an element that requires action from the user.
 *
 * Built on top of MUI Drawer component to provide the same API as the original pounce SideSheet
 * while leveraging MUI's accessibility and animation patterns.
 */
export const SideSheet = React.forwardRef<HTMLDivElement, SideSheetProps>(function SideSheet(
  {
    children,
    open,
    onClose,
    hideDismissIcon = false,
    containerProps,
    transitionDuration = 500,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    anchor = 'right',
    width,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps: containerPounceProps = {} } = containerProps
    ? extractPounceProps(containerProps)
    : { pounceProps: {} };
  // Width on the inner Box (via containerProps) does not size the MUI Drawer paper. The paper
  // uses flex-basis: auto, so it grows with content. A right-anchored paper then extends left;
  // very wide content (e.g. nowrap table columns) can push the panel and close control off-screen.
  // Apply width/min/max from containerProps to the paper, and cap horizontal sheets at the viewport.
  const {
    width: containerWidth,
    minWidth: containerMinWidth,
    maxWidth: containerMaxWidth,
    ...innerPounceFromContainer
  } = containerPounceProps;
  const containerPounceStyles = mapPouncePropsToSx(innerPounceFromContainer);
  const theme = useTheme();
  const tokens = theme.palette?.tokens;

  const isHorizontal = anchor === 'left' || anchor === 'right';
  const isVertical = anchor === 'top' || anchor === 'bottom';

  const paperWidth = width ?? containerWidth;
  const paperMinWidth = containerMinWidth;
  const paperMaxWidth = containerMaxWidth ?? (isHorizontal ? '100vw' : undefined);

  // Default inner min width is for generic sheets. When containerProps sets maxWidth (e.g.
  // NotificationSidesheet at 510px), imposing 560px here would force the panel wider than intended
  // after width/min/max move to the drawer paper.
  const innerMinWidth =
    isHorizontal && containerMaxWidth === undefined && containerMinWidth === undefined
      ? '560px'
      : undefined;

  const sxBeforePaperStyles = drawerSxToArray(sx);

  return (
    <Drawer
      ref={ref}
      anchor={anchor}
      open={open}
      onClose={onClose}
      transitionDuration={transitionDuration}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      // Consumer `sx` often targets `& .MuiDrawer-paper` (e.g. top offset for the notification
      // drawer). Spreading sx after a single paper object would replace the entire selector and
      // drop width/maxWidth — use array merge so paper sizing stays applied.
      sx={[
        ...sxBeforePaperStyles,
        {
          '& .MuiDrawer-paper': {
            // Keep wide content inside the sheet; inner Box already uses overflow: auto
            overflow: isHorizontal ? 'hidden' : 'visible',
            width: paperWidth,
            minWidth: paperMinWidth,
            maxWidth: paperMaxWidth,
          },
        },
      ]}
      {...rest}
    >
      <Box
        p={6}
        minWidth={innerMinWidth}
        width={isVertical ? '100vw' : '100%'}
        minHeight={isVertical ? '200px' : undefined}
        bg={tokens.background.brand.dark.base}
        position="relative"
        height={isHorizontal ? '100vh' : 'auto'}
        shadow={theme.shadows?.[24]}
        overflow="auto"
        borderLeft={`1px solid ${tokens.stroke.neutral[5]}`}
        sx={containerPounceStyles}
      >
        {!hideDismissIcon && (
          <Box position="absolute" top={16} right={16} zIndex={1}>
            <IconButton
              icon="close"
              aria-label="Dismiss Dialog"
              variant="ghost"
              size="medium"
              onClick={onClose}
            />
          </Box>
        )}
        {children}
      </Box>
    </Drawer>
  );
});
